/**
 * `aethereum mission` — the interactive mission-control cockpit.
 *
 * A fork of `aethereum watch`: the same catch-up + HostedSubscriber read spine
 * feeds `buildWatchModel`, so the room (directive, agents, tickets, contracts,
 * plan, claims, proposals, activity) renders live. On top of the read spine it
 * constructs a `HostedWriteClient` and wires a bottom COMMAND BAR: the operator
 * types slash-commands that push directives / prompts / tickets into the room.
 * Writes echo back over the read spine, so panels refresh on their own; the
 * cockpit also shows an optimistic ack the moment a command lands.
 */

import { HiveRoomState } from "@br9704/aethereum-shared";
import { HostedSubscriber } from "../../hive/subscriber.js";
import { HostedWriteClient } from "../../hive/hosted-client.js";
import { buildWatchModel, FLASH_MS, type WatchStatus } from "../watch/model.js";
import { catchUp, resolveWatchConfig } from "../watch/index.js";
import { parseCommand } from "./commands.js";

export interface MissionFlags {
  url?: string;
  token?: string;
  room?: string;
  fetchImpl?: typeof fetch;
}

/** Short (8-char) ticket id for acks, mirroring the model + ticket CLI. */
function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}

export async function runMission(flags: MissionFlags): Promise<void> {
  const cfg = resolveWatchConfig(flags);
  if ("error" in cfg) {
    console.error(cfg.error);
    process.exit(1);
    return;
  }

  const fetchImpl = flags.fetchImpl ?? fetch;
  const state = new HiveRoomState();
  const { cursor } = await catchUp(cfg, state, fetchImpl);

  const write = new HostedWriteClient(cfg.baseUrl, cfg.token, fetchImpl);

  let status: WatchStatus = "connecting";
  let lastCursor = cursor;
  const flashes = new Map<string, number>();

  const { startMissionApp } = await import("./app.js");

  const buildModel = () =>
    buildWatchModel(state, {
      flashes,
      status,
      now: Date.now(),
      roster: subscriber.roster,
      cursor: lastCursor,
    });

  const subscriber = new HostedSubscriber({
    baseUrl: cfg.baseUrl,
    token: cfg.token,
    since: cursor,
    includeOwn: true,
    fetchImpl,
    onEvent: ({ event, id }) => {
      lastCursor = id;
      if (event.type === "contract_changed") {
        flashes.set(event.name, Date.now() + FLASH_MS);
        const t = setTimeout(() => app.update(buildModel()), FLASH_MS + 50);
        if (typeof t === "object" && "unref" in t) t.unref();
      } else {
        state.ingest(event);
      }
      app.update(buildModel());
    },
    onStatus: (s) => {
      status = s;
      app.update(buildModel());
    },
  });

  /**
   * Parse one operator line and drive the matching write. Friendly acks on the
   * cockpit; the read spine echoes the change back to refresh the panels.
   */
  const onSubmit = async (line: string): Promise<void> => {
    const cmd = parseCommand(line);
    try {
      switch (cmd.kind) {
        case "noop":
          return;
        case "help":
          app.ack(
            "/directive <text> · /clear · /prompt [@who] <text> · /ticket [@who] <title> · /assign <id> <who> · /doing|/done|/close <id>",
            "ok",
          );
          return;
        case "error":
          app.ack(`✗ ${cmd.message}`, "err");
          return;
        case "directive":
          await write.setDirective(cmd.text, cmd.targets);
          app.ack(cmd.targets?.length ? `✓ directive set for ${cmd.targets.join(", ")}` : "✓ directive set", "ok");
          return;
        case "clear":
          await write.clearDirective();
          app.ack("✓ directive cleared", "ok");
          return;
        case "prompt": {
          const to = await write.prompt(cmd.text, cmd.to);
          app.ack(
            cmd.to
              ? `✓ prompt sent to ${to ?? cmd.to} (live to running sessions)`
              : "✓ prompt broadcast (live to running sessions)",
            "ok",
          );
          return;
        }
        case "ticket": {
          const { ticketId } = await write.createTicket({ title: cmd.title, assignee: cmd.assignee });
          app.ack(`✓ ticket #${shortId(ticketId)} created`, "ok");
          return;
        }
        case "assign":
          await write.updateTicket({ ticketId: cmd.ticketId, assignee: cmd.assignee });
          app.ack(`✓ #${shortId(cmd.ticketId)} assigned to ${cmd.assignee}`, "ok");
          return;
        case "ticket_status":
          await write.updateTicket({ ticketId: cmd.ticketId, status: cmd.status });
          app.ack(`✓ #${shortId(cmd.ticketId)} → ${cmd.status}`, "ok");
          return;
      }
    } catch (err) {
      app.ack(`✗ ${err instanceof Error ? err.message : String(err)}`, "err");
    }
  };

  const quit = () => {
    subscriber.stop();
    clearInterval(tick);
    app.stop();
    process.exit(0);
  };

  const app = startMissionApp({
    room: cfg.room,
    initialModel: buildModel(),
    onQuit: quit,
    onSubmit,
  });
  process.on("SIGINT", quit);
  process.on("SIGTERM", quit);

  const tick = setInterval(() => app.update(buildModel()), 10_000);

  await subscriber.start();
}
